import { useState, useEffect, useRef, useCallback } from "react";
import { FaLine } from "react-icons/fa";
import { onAuthStateChanged } from "firebase/auth";
import { auth as firebaseAuth } from "../firebase";
import {
  incrementLineApply,
  getTotalApplyCount,
  getHasApplied,
} from "../utils/incrementLineApply";

const BASE_LINE_URL = "/go/line.html";

// --- GA4 直送ユーティリティ（debug_mode 付与） ---
const isDebug = () => {
  try {
    const usp = new URLSearchParams(window.location.search);
    if (usp.get("debug_mode") === "1") return true;
    return localStorage.getItem("ga_debug_mode") === "1";
  } catch { return false; }
};
const trackGA = (event, params = {}) => {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", event, { ...params, debug_mode: isDebug() });
    }
  } catch {}
};

// 推奨イベント generate_lead（軽デバウンス）
const leadDebounce = { lastSentAt: 0 };
const trackLead = (stage = "click") => {
  const now = Date.now();
  if (now - leadDebounce.lastSentAt < 500) return;
  leadDebounce.lastSentAt = now;
  trackGA("generate_lead", {
    method: "line",
    form_id: "line-apply-button",
    stage, // "click" | "counter_recorded"
  });
};

// アプリ内ブラウザ検知（安全なUA判定のみ）
const isInApp = () => {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) ? navigator.userAgent : "";
  const isInstagram = /Instagram/i.test(ua);
  const isFacebook  = /FBAN|FBAV|Facebook/i.test(ua);
  const isLineInApp = /Line\//i.test(ua);
  const isTwitterX  = /(Twitter|X\/|MiuiBrowser)/i.test(ua);
  return {
    isInstagram,
    isFacebook,
    isLineInApp,
    isTwitterX,
    any: isInstagram || isFacebook || isLineInApp || isTwitterX,
  };
};

// 計測用クエリを付けた遷移先URLを生成
const buildTrackedUrl = () => {
  const uid = localStorage.getItem("anonUID") || "anon";
  const ts  = Date.now().toString();
  const usp = new URLSearchParams({
    src: "cta",
    uid,
    ts,
    utm_source: "site",
    utm_medium: "cta",
    utm_campaign: "line",
  });
  return `${BASE_LINE_URL}?${usp.toString()}`;
};

const LineApplyButton = () => {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [hasApplied, setHasApplied] = useState(false);
  const [applyCount, setApplyCount] = useState(0);
  const [visible, setVisible] = useState(false);
  const [showFallbackLink, setShowFallbackLink] = useState(false);
  const [animPlusOne, setAnimPlusOne] = useState(false);
  const [animGlow, setAnimGlow] = useState(false);
  const [showInAppHelp, setShowInAppHelp] = useState(false);

  const timeouts = useRef([]);
  const openedRef = useRef(false);
  const mountedRef = useRef(true);
  const inApp = useRef(isInApp());

  useEffect(() => {
    mountedRef.current = true;
    const unsub = onAuthStateChanged(firebaseAuth, (u) => {
      if (!mountedRef.current) return;
      setUser(u);
      setLoadingAuth(false);
    });
    return () => {
      mountedRef.current = false;
      unsub();
    };
  }, []);

  // 15秒後に表示
  useEffect(() => {
    const id = window.setTimeout(() => setVisible(true), 15000);
    timeouts.current.push(id);
    return () => { timeouts.current.forEach(clearTimeout); timeouts.current = []; };
  }, []);

  // タブが非表示になった＝外部遷移成功とみなす
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        openedRef.current = true;
        trackGA("line_open_success", { // ← 明示
          button_id: "line-apply-button",
        });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // 初期データ
  useEffect(() => {
    if (!user || !visible) return;
    let cancelled = false;

    getTotalApplyCount().then((n) => {
      if (!cancelled && mountedRef.current) setApplyCount(n);
    });

    getHasApplied(user).then((applied) => {
      if (!cancelled && mountedRef.current) {
        setHasApplied(applied);
        const t1 = window.setTimeout(() => setAnimGlow(true), 200);
        const t2 = window.setTimeout(() => setAnimGlow(false), 1200);
        timeouts.current.push(t1, t2);
      }
    });

    return () => { cancelled = true; };
  }, [user, visible]);

  const tryOpenLine = useCallback(() => {
    const lineUrl = buildTrackedUrl();

    try {
      const win = window.open(lineUrl, "_blank", "noopener,noreferrer");
      if (win) {
        const t = window.setTimeout(() => {
          if (!openedRef.current && document.visibilityState === "visible") {
            window.location.href = lineUrl;
          }
        }, 900);
        timeouts.current.push(t);
      } else {
        window.location.href = lineUrl;
      }
    } catch {
      window.location.href = lineUrl;
    }

    const failCheck = window.setTimeout(() => {
      if (!openedRef.current && document.visibilityState === "visible") {
        trackGA("line_open_failed", {
          button_id: "line-apply-button",
          link_url: lineUrl,
          in_app: inApp.current.any ? "1" : "0",
        });
        if (inApp.current.any) {
          setShowInAppHelp(true);
          trackGA("line_inapp_help_shown");
        } else {
          setShowFallbackLink(true);
          trackGA("line_fallback_shown");
        }
      }
    }, 1500);
    timeouts.current.push(failCheck);
  }, []);

  const handleApplyClick = useCallback(async (e) => {
    if (e) e.preventDefault();

    const lineUrl = buildTrackedUrl();

    // クリックイベント
    trackGA("line_apply_click", {
      button_id: "line-apply-button",
      link_url: lineUrl,
      in_app: inApp.current.any ? "1" : "0",
    });
    trackLead("click");

    // カウント
    if (user && !hasApplied) {
      try {
        const result = await incrementLineApply(user);
        if (mountedRef.current && result && result.status === "applied") {
          setApplyCount((c) => c + 1);
          setHasApplied(true);
          setAnimPlusOne(true);
          const animTimeout = window.setTimeout(() => setAnimPlusOne(false), 1000);
          timeouts.current.push(animTimeout);

          trackLead("counter_recorded");
        }
      } catch (err) {
        console.error("応募処理エラー:", err);
      }
    }

    tryOpenLine();
    if (inApp.current.any) {
      setShowInAppHelp(true);
      trackGA("line_inapp_help_shown");
    }
  }, [user, hasApplied, tryOpenLine]);

  // ESCで閉じる
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === "Escape") {
        setShowInAppHelp(false);
        setShowFallbackLink(false);
        trackGA("line_help_closed");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const copyLink = async () => {
    const lineUrl = buildTrackedUrl();
    try {
      await navigator.clipboard.writeText(lineUrl);
      trackGA("line_copy_link", { button_id: "line-apply-button", link_url: lineUrl });
      alert("LINEのリンクをコピーしました。外部ブラウザで貼り付けて開いてください。");
    } catch {
      alert("コピーに失敗しました。長押しでURLをコピーしてください。\n" + lineUrl);
    }
  };

  const openInExternal = () => {
    const lineUrl = buildTrackedUrl();
    try {
      const w = window.open(lineUrl, "_blank", "noopener,noreferrer");
      trackGA("line_open_external", {
        button_id: "line-apply-button",
        link_url: lineUrl,
        opened: w ? "1" : "0",
      });
      if (!w) window.location.href = lineUrl;
    } catch {
      window.location.href = lineUrl;
    }
  };

  if (loadingAuth || !visible) return null;

  return (
    <>
      <style>{`
        @keyframes riseFade {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(-24px); opacity: 0; }
        }
        @keyframes glowGreen {
          0%,100% { box-shadow: 0 0 12px rgba(0,255,0,0.4), 0 0 20px rgba(0,255,0,0.2); }
          50%     { box-shadow: 0 0 16px rgba(0,255,0,0.6), 0 0 24px rgba(0,255,0,0.3); }
        }
        @keyframes glowCount {
          0%,100% { transform: scale(1); opacity: 1; }
          50%    { transform: scale(1.15); opacity: 0.85; }
        }
        .modal-backdrop {
          position: fixed; inset: 0; background: rgba(0,0,0,.45);
          display: flex; align-items: center; justify-content: center; z-index: 1100;
        }
        .modal {
          width: min(92vw, 480px);
          background: #fff; border-radius: 16px; padding: 18px;
          box-shadow: 0 10px 30px rgba(0,0,0,.2);
        }
        .modal h3 { margin: 0 0 10px; font-size: 18px; }
        .modal p { margin: 8px 0; font-size: 14px; line-height: 1.7; }
        .modal .btns { display: grid; gap: 10px; margin-top: 12px; }
        .btn {
          appearance: none; border: none; border-radius: 9999px;
          padding: 12px 20px; font-weight: 700; font-size: 15px; cursor: pointer;
        }
        .btn-primary { background: #00c300; color: #fff; }
        .btn-secondary { background: #f1f3f5; color: #222; }
        .btn-danger { background: #e91e63; color: #fff; }
        .close-x { position: absolute; right: 10px; top: 8px; font-size: 20px; background: transparent; border: none; cursor: pointer; color: #999; }
      `}</style>

      <div style={styles.container} role="region" aria-label="LINE応募ボタン">
        <div style={styles.innerWrapper}>
          <div style={{ position: "relative" }}>
            <button
              id="line-apply-button"
              onClick={handleApplyClick}
              style={{ ...styles.button, ...(hasApplied ? styles.disabledButton : {}) }}
              title="LINEで応募・相談する"
              aria-label="LINEで応募・相談する"
              type="button"
            >
              <FaLine style={styles.icon} aria-hidden="true" />
              <span style={styles.labelWrapper}>
                <span style={styles.text}>LINEで応募・相談する</span>
                <span
                  style={{ ...styles.inlineApplyCount, ...(animGlow ? styles.applyCountGlow : {}) }}
                  aria-live="polite"
                >
                  {`${applyCount}人`}
                </span>
              </span>
            </button>
            {animPlusOne && <div style={styles.plusOne} aria-hidden="true">+1</div>}
          </div>
        </div>

        {showFallbackLink && (
          <div style={styles.fallbackWrapper} role="alert">
            <p style={styles.fallbackText}>
              ⚠ LINEにうまく遷移できない場合は、<br />
              <strong>下のボタンから直接開く</strong>か、<strong>リンクをコピー</strong>してください。
            </p>
            <div style={{ display: "grid", gap: 10, justifyContent: "center" }}>
              <button className="btn btn-primary" onClick={openInExternal}>LINEを開く</button>
              <button className="btn btn-secondary" onClick={copyLink}>リンクをコピー</button>
            </div>
          </div>
        )}
      </div>

      {showInAppHelp && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="アプリ内ブラウザでの開き方">
          <div className="modal" style={{ position: "relative" }}>
            <button
              className="close-x"
              aria-label="閉じる"
              onClick={() => { setShowInAppHelp(false); trackGA("line_inapp_help_closed"); }}
            >
              {"\u00D7"}
            </button>
            <h3>アプリ内ブラウザではLINEが開きにくいことがあります</h3>
            <p>右上のメニューから <strong>「ブラウザーで開く」</strong>（iOSは「Safariで開く」）を選ぶと開けます。</p>
            <ol style={{ margin: "8px 0 0 18px", padding: 0, color: "#444" }}>
              <li>画面右上の「…」をタップ</li>
              <li>「ブラウザーで開く / Safariで開く」を選択</li>
              <li>もう一度「LINEで応募・相談する」をタップ</li>
            </ol>
            <div className="btns">
              <button className="btn btn-primary" onClick={openInExternal}>外部ブラウザで開く</button>
              <button className="btn btn-secondary" onClick={copyLink}>リンクをコピー</button>
              <button className="btn btn-danger" onClick={() => { setShowInAppHelp(false); trackGA("line_inapp_help_closed"); }}>閉じる</button>
            </div>
            <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
              URL：<br />
              <code style={{ wordBreak: "break-all" }}>{BASE_LINE_URL}</code>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  container: {
    position: "fixed",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1000,
    bottom: "max(4vh, 60px)",
    width: "100%",
    padding: "0 16px",
  },
  innerWrapper: { maxWidth: "420px", margin: "0 auto" },
  button: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "12px 16px",
    background: "linear-gradient(135deg, #00c300, #00e676)",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "bold",
    borderRadius: "12px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    animation: "glowGreen 2.4s ease-in-out infinite",
    position: "relative",
    boxShadow: "0 4px 12px rgba(0, 195, 0, 0.25)",
    textAlign: "center",
    lineHeight: 1.4,
  },
  disabledButton: { opacity: 0.75 },
  icon: { fontSize: "22px" },
  labelWrapper: { display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" },
  text: { fontSize: "15px", fontWeight: "bold", lineHeight: 1.4, display: "flex", alignItems: "center" },
  inlineApplyCount: {
    minWidth: "24px",
    height: "24px",
    padding: "0 6px",
    fontSize: "13px",
    color: "#00c300",
    backgroundColor: "#fff",
    borderRadius: "9999px",
    fontWeight: "bold",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  applyCountGlow: { animation: "glowCount 0.8s ease-in-out 1" },
  plusOne: {
    position: "absolute",
    top: "-18px",
    right: 0,
    color: "#fff",
    fontSize: "16px",
    fontWeight: "bold",
    backgroundColor: "#e91e63",
    borderRadius: "12px",
    padding: "4px 8px",
    animation: "riseFade 0.9s ease-out",
    pointerEvents: "none",
    zIndex: 10,
    whiteSpace: "nowrap",
  },
  fallbackWrapper: {
    textAlign: "center",
    marginTop: "24px",
    padding: "16px",
    backgroundColor: "#e6f5ec",
    border: "2px solid #00c300",
    borderRadius: "12px",
  },
  fallbackText: { fontSize: "16px", fontWeight: "bold", color: "#006400", marginBottom: "12px", lineHeight: 1.6 },
};

export default LineApplyButton;
